import * as React from "react"
import { ChevronDown, Check, Search } from "lucide-react"
import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface SearchableSelectOption {
  value: string
  label: string
  /** Short secondary line under the label (e.g. email). */
  hint?: string
}

interface SearchableSelectProps {
  options: SearchableSelectOption[]
  value: string
  onChange: (value: string) => void
  /** Lands on the trigger, so a <Label htmlFor> can name the control. */
  id?: string
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  className?: string
  disabled?: boolean
  /**
   * Set when the select lives inside a modal Sheet/Dialog: the dialog's
   * scroll lock swallows wheel events on portaled content, so the option list
   * cannot scroll. A modal popover brings its own scroll scope.
   */
  modalPopover?: boolean
  /**
   * Set when `options` is a server-side page rather than the whole set: the
   * local filter is skipped (it would filter an already-filtered page and hide
   * matches that are simply not loaded) and the term is reported instead.
   */
  onSearchChange?: (term: string) => void
  /** Whether a server-side search is in flight; only meaningful with onSearchChange. */
  loading?: boolean
  loadingText?: string
}

/**
 * Single-value Select with a search box in the dropdown — the one-value
 * sibling of MultiSelect (same trigger/popover styling). Use it where a plain
 * Select would force scanning a long list (people, companies, accounts).
 */
export function SearchableSelect({
  options,
  value,
  onChange,
  id,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyText = "Nothing found",
  className,
  disabled = false,
  modalPopover = false,
  onSearchChange,
  loading = false,
  loadingText = "Searching...",
}: SearchableSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")
  // The option the keyboard is on. -1 means "none yet", so the first ArrowDown
  // lands on the first row rather than skipping it.
  const [activeIndex, setActiveIndex] = React.useState(-1)
  const listId = React.useId()
  const listRef = React.useRef<HTMLDivElement>(null)
  // Only the keyboard may scroll the list. The pointer moving over rows also
  // moves the highlight, and scrolling the wheel drags rows under a stationary
  // pointer — so without this the list scrolls itself while the user scrolls it.
  const keyboardNav = React.useRef(false)

  const selectedOption = options.find((option) => option.value === value)

  const handleSearch = (term: string) => {
    setSearch(term)
    setActiveIndex(-1)
    onSearchChange?.(term)
  }

  const filteredOptions = React.useMemo(() => {
    const term = search.trim().toLowerCase()
    if (onSearchChange || !term) return options
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(term) ||
        option.hint?.toLowerCase().includes(term)
    )
  }, [options, search, onSearchChange])

  // A server-side page can arrive shorter than the highlight's index.
  React.useEffect(() => {
    setActiveIndex((current) => (current >= filteredOptions.length ? -1 : current))
  }, [filteredOptions.length])

  const handleSelect = (option: SearchableSelectOption) => {
    onChange(option.value)
    setOpen(false)
  }

  const optionId = (index: number) => `${listId}-option-${index}`

  const moveActive = (delta: number) => {
    if (filteredOptions.length === 0) return
    keyboardNav.current = true
    setActiveIndex((current) => {
      const next = current + delta
      if (next < 0) return filteredOptions.length - 1
      if (next >= filteredOptions.length) return 0
      return next
    })
  }

  // Keep the highlight visible without stealing focus from the search box —
  // the input keeps it throughout, which is what aria-activedescendant is for.
  // Indexed off the children rather than by id: useId() emits colons, which a
  // querySelector would need escaping for, and the rows are index-aligned
  // whenever there is a highlight at all.
  React.useEffect(() => {
    if (activeIndex < 0 || !keyboardNav.current) return
    keyboardNav.current = false
    listRef.current?.children[activeIndex]?.scrollIntoView({ block: "nearest" })
  }, [activeIndex])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        moveActive(1)
        break
      case "ArrowUp":
        event.preventDefault()
        moveActive(-1)
        break
      case "Home":
        if (filteredOptions.length === 0) break
        event.preventDefault()
        keyboardNav.current = true
        setActiveIndex(0)
        break
      case "End":
        if (filteredOptions.length === 0) break
        event.preventDefault()
        keyboardNav.current = true
        setActiveIndex(filteredOptions.length - 1)
        break
      case "Enter": {
        const option = filteredOptions[activeIndex]
        if (!option) break
        event.preventDefault()
        handleSelect(option)
        break
      }
      default:
        break
    }
  }

  return (
    <Popover
      modal={modalPopover}
      open={disabled ? false : open}
      onOpenChange={(isOpen) => {
        if (disabled) return
        setOpen(isOpen)
        if (!isOpen) {
          handleSearch("")
          setActiveIndex(-1)
        }
      }}
    >
      <PopoverTrigger asChild>
        {/* A real button, not a div: the trigger has to be reachable by Tab
            and operable by Enter/Space, and announce that it opens a list. */}
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-left text-sm ring-offset-background cursor-pointer hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            disabled && "opacity-50 cursor-not-allowed hover:bg-white",
            className
          )}
        >
          {selectedOption ? (
            <span className="truncate">{selectedOption.label}</span>
          ) : (
            <span className="text-muted-foreground truncate">{placeholder}</span>
          )}
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      {/*
        The list follows the trigger's width, but never narrower than a name plus
        the search box: a trigger sized to its slot (the nationality cell in a
        document row is w-36) would otherwise clip every option.

        As CLASSES, not as a style attribute. `PopoverContent` carries `w-72` in
        its base class, and every Tailwind utility in this project compiles with
        `!important` (tailwind.config.js) — which beats an inline width. So the
        list was 18rem wide wherever it was used, whatever its trigger, and long
        options were truncated in a dialog with room to spare. `cn()` drops the
        base `w-72` in favour of the one below.
      */}
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[240px] p-0"
        align="start"
      >
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              role="combobox"
              aria-expanded={open}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              className="pl-8 h-9"
            />
          </div>
        </div>
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          aria-busy={loading || undefined}
          className="max-h-60 overflow-auto p-1"
        >
          {loading && filteredOptions.length === 0 ? (
            <div className="px-2 py-4 text-sm text-neutral-600 text-center">{loadingText}</div>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => {
              const isSelected = option.value === value
              const isActive = index === activeIndex
              return (
                <div
                  key={option.value}
                  id={optionId(index)}
                  role="option"
                  aria-selected={isSelected}
                  className={cn(
                    "flex items-start gap-2 px-2 py-2 rounded-sm cursor-pointer hover:bg-accent",
                    isSelected && "bg-accent/50",
                    isActive && "bg-accent"
                  )}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  <div className="h-4 w-4 shrink-0 flex items-center justify-center mt-0.5">
                    {isSelected && <Check className="h-4 w-4 text-primary" />}
                  </div>
                  <div className="min-w-0">
                    <span className="text-sm block truncate">{option.label}</span>
                    {option.hint && (
                      <span className="text-xs text-muted-foreground block truncate">{option.hint}</span>
                    )}
                  </div>
                </div>
              )
            })
          ) : (
            <div className="px-2 py-4 text-sm text-neutral-600 text-center">{emptyText}</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
